import { Close, EditSharp, VisibilityRounded } from "@mui/icons-material";
import { Button, Stack, Typography } from "@mui/material";
import { DataGridPremiumProps, GridActionsCellItem, gridClasses, GridColDef, GridRowId, GridRowParams, useGridApiRef } from "@mui/x-data-grid-premium";
import { useEffect, useMemo, useState } from "react";
import { URLSearchParamsInit, useNavigate, useSearchParams } from "react-router-dom";
import { ResponseArray, ResponseObject } from "../common/common.entity";
import { formatHeaderName } from "../common/commonFunctions";
import { CatalogueGrid, MuiPage, MuiPopup } from "../components";
import { IFormBuilder } from "../components/formBuilder/formBuilder.entity";
import MuiRichTreeView from "../components/treeView/MuiRichTreeView";
import { setReducerState } from "../redux/crudSlice";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { defaultState, ICatalogBuilder } from "./catalogBuilder.entity";
import { GroupCatalogHeader } from "./groupCatalog.header";

const defaultColDef: Partial<GridColDef> = {
  flex: 1,
  minWidth: 130,
  headerAlign: "left",
}

const InitSearchParams: URLSearchParamsInit = [
  ['mode', '']
]

const defaultHiddenColumns = {
  id: false,
  IsActive: false,
  IsDeleted: false,
  CreatedOn: false,
  CreatedBy: false,
  UpdatedBy: false,
  UpdatedOn: false,
  PartitionKey: false
}

export const CatalogBuilder = (props: ICatalogBuilder) => {
  const {
    headerLabel,
    headerButtonLabel,

    getPagingData,
    onDelete,
    pagingAPIKey = "",
    pagingAPIrequest = {},

    customPagingValue,
    pagingColumns = [],
    onModeClickPath = "",
    isAssociationButton = false,
    onClickPlus,
    isRightMenu = false,
    isDefaultActionButton = true,
    isHeaderButton = true,
    isPlusButton = true,
    isFooterButton,
    footerButtonLabel,
    disableEditAction = false,
    disableDeleteAction = false,
    disableViewAction = false,

    getRowIdByPaging = 'id',
    hideFooterSelectedRowCount = false,
    hideFooterPagination = false,
    pageSizeOptions,

    getTreeData,
    treeApiKey,
    treeData,
    treeViewLabel,
    dataGridProps,
  } = props;

  let { mode = "edit", visibleColumns = {} } = props

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams(InitSearchParams);
  mode = searchParams.get('mode') || mode;

  const [selectedRowsCount, setSelectedRowsCount] = useState(0)

  const updateKey = pagingAPIKey + 'Updated'
  const crud = useAppSelector((state) => state.crud);
  const gridApi = useGridApiRef()

  let rowData = useMemo(() => {
    let data: ResponseArray = (customPagingValue || crud[pagingAPIKey]?.data);
    data = Array.isArray(data) ? data?.filter((row) => !!row?.id) : [];
    return data
  }, [crud[pagingAPIKey]?.data, customPagingValue])

  const [state, setState] = useState({ ...defaultState });
  const [onDeleteId, setOnDeleteId] = useState<any>();

  visibleColumns = useMemo(() => {
    if (!rowData[0]) return defaultHiddenColumns;
    let obj = { ...(rowData[0] || {}) }
    Object.keys(obj).forEach(k => {
      if (k.substring(k.length - 2) === 'Id' || k[0] === '_')
        obj[k] = false
      else
        obj[k] = true
    })
    let res = { ...obj, ...defaultHiddenColumns, ...visibleColumns }
    gridApi?.current?.setColumnVisibilityModel?.(res)
    return res
  }, [rowData]);


  useEffect(() => {
    if (!customPagingValue) {
      getGroupPaging(state);
    }
  }, []);

  useEffect(() => {
    if (crud[updateKey]) {
      getGroupPaging(state);
    }
  }, [crud[updateKey]])

  const getGroupPaging = async (_state: any) => {
    const _request = {
      ..._state,
      ...pagingAPIrequest,
    };
    const res = await getPagingData(dispatch, _request);
    dispatch(setReducerState({ key: updateKey, value: false }));
  };

  const onPageSizeChange = async ({ page, pageSize }: any) => {
    let _state: any = {
      ...state,
      skip: page * pageSize,
      take: pageSize,
    };
    getGroupPaging(_state);
    setState(_state);
  };

  const onSortChange = async (sortModel: any) => {
    let sortBy = {
      SortType: sortModel[0]?.field ? sortModel[0]?.field : null,
      ASC: sortModel[0]?.sort === "asc" ? true : false,
    };
    let _state = { ...state, sortBy: sortBy };
    getGroupPaging(_state);
    setState(_state);
  };

  const onFilterModelChange: DataGridPremiumProps['onFilterModelChange'] = (searchModel, details) => {
    if (!details.reason) return;

    let _search = searchModel.items[0]?.value;
    let _state = {
      ...state,
      search: _search,
    };

    getGroupPaging(_state);
    setState(_state);
  };

  const onModeClick = (params: GridRowParams | null, mode: IFormBuilder['mode']) => {
    const rowId = params?.row?.id || "";
    if (onClickPlus) onClickPlus(params?.row, mode);
    if (onModeClickPath && !isRightMenu) {
      const url = `${onModeClickPath}${rowId}`;
      searchParams.set('mode', (mode || ''));
      navigate({ pathname: url, search: searchParams.toString() });
    }
  };

  const handleDelete = async (id: GridRowId) => {
    setOnDeleteId(id);
  };

  const handleOnDelete = async (e: any) => {
    if (!onDelete || disableDeleteAction) return;
    await onDelete(dispatch, onDeleteId);
    onCloseDeletePopup();
    await getGroupPaging(state);
  };

  const onCloseDeletePopup = () => {
    setOnDeleteId(null);
  };

  const handleMultiDelete = async () => {
    if (!onDelete) return;
    const selectedRows = gridApi.current?.getSelectedRows?.().keys().toArray()

    if (!selectedRows?.length) return;

    await Promise.all(
      selectedRows?.map(rowId => onDelete(dispatch, rowId)).filter(Boolean)
    )
    await getGroupPaging(state);
  }

  const columns: ICatalogBuilder['pagingColumns'] = useMemo(() => {
    let fields: ResponseObject = {};
    rowData?.forEach((r: ResponseObject) => { fields = { ...fields, ...r }; })
    fields = Object.keys(fields);
    if (!fields?.length) fields = ['Name', 'Code']

    const genCol: GridColDef[] = fields.map((item: string) => {
      const type = item.includes('Date') ? 'date' : 'string';
      return {
        ...defaultColDef,
        type,
        field: item,
        headerName: formatHeaderName(item),
        valueGetter: (value: any) => {
          let newValue = value;
          if (value && !Array.isArray(value) && typeof value === 'object') {
            newValue = newValue?.Name || newValue?.DisplayName;
          }
          if (value && type === 'date') {
            newValue = new Date(value)
          }
          return newValue;
        },
        filterable: item === 'Name' || item === 'DisplayName'
      }
    });
    genCol.push(...pagingColumns as GridColDef[])

    if (isDefaultActionButton) {
      genCol.push({
        type: 'actions',
        field: "actionIcons",
        headerName: "Actions",
        width: 120,
        headerAlign: "left",
        align: 'left',
        sortable: false,
        disableColumnMenu: true,
        filterable: false,
        resizable: false,
        getActions: (params) =>
          [
            !(mode === 'view' || disableEditAction) ?
              [
                <GridActionsCellItem
                  id={`edit`}
                  icon={<EditSharp sx={{ fontSize: 16 }} />}
                  label="Edit"
                  onClick={() => onModeClick(params, 'edit')}
                  size='small'
                />
              ] : [],
            !disableViewAction ?
              [
                <GridActionsCellItem
                  id={`view`}
                  icon={<VisibilityRounded sx={{ fontSize: 16 }} />}
                  label="View"
                  onClick={() => onModeClick(params, 'view')}
                  size='small'
                />
              ] : [],
            !disableDeleteAction ?
              [
                <GridActionsCellItem
                  id={`delete`}
                  icon={<Close sx={{ fontSize: 16 }} />}
                  label="Delete"
                  onClick={() => handleDelete(params.id)}
                  // onClick={() => onModeClick(params, 'delete')}
                  size='small'
                />
              ] : [],
          ].flat()
      });
    }

    return genCol;
  }, [pagingColumns?.length, rowData]);

  return (
    <MuiPage>
      <GroupCatalogHeader
        headerLabel={headerLabel}
        addButtonLabel={headerButtonLabel}
        onAdd={(e, mode) => { onModeClick(null, mode) }}
        onRefresh={() => getGroupPaging(state)}
        isHeaderButton={isHeaderButton}
        disableAdd={(mode === "view") || !isPlusButton}
        isAssociationButton={isAssociationButton}
        onDelete={handleMultiDelete}
        disableDelete={!Boolean(selectedRowsCount)}
      />

      <Stack sx={{ minHeight: 300 }} >
        <CatalogueGrid
          apiRef={gridApi}
          getRowId={(row: ResponseObject) => row?.[getRowIdByPaging]}
          onPaginationModelChange={onPageSizeChange}
          rows={rowData}
          columns={isDefaultActionButton ? columns : pagingColumns}
          rowCount={crud[pagingAPIKey]?.count}
          loading={crud[`isLoading${pagingAPIKey}`] === 'loading'}
          // onSortModelChange={onSortChange}
          // onFilterModelChange={onFilterModelChange}
          hideFooterSelectedRowCount={hideFooterSelectedRowCount}
          hideFooterPagination={hideFooterPagination}
          pageSizeOptions={pageSizeOptions}
          filterDebounceMs={500}
          rowHeight={40}
          columnHeaderHeight={40}
          showCellVerticalBorder
          filterMode='client'
          onRowSelectionModelChange={(rowSelectionModel) => { setSelectedRowsCount(rowSelectionModel?.ids?.size) }}
          {...dataGridProps}
          initialState={{
            ...dataGridProps?.initialState,
            pagination: {
              paginationModel: {
                pageSize: pagingAPIrequest?.take || 10,
                page: 0,
              },
            },
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton'
            },
          }}
          sx={{
            borderRadius: 2,
            [`.${gridClasses.toolbar} button[aria-label=Export],.${gridClasses.toolbarDivider}`]: {
              display: 'none'
            },
            [`.${gridClasses.toolbarLabel}`]: {
              typography: 'h1'
            },
            [`.${gridClasses.columnHeader}`]: {
              bgcolor: 'secondary.100',
              typography: 'h5'
            },
          }}
        />
      </Stack>


      {isFooterButton &&
        <Button
          size="small"
          variant='contained'
          children={(footerButtonLabel || "Add Item")}
          sx={{ width: '100%', mt: 2 }}
          onClick={(e) => onModeClick(null, "add")}
        />}

      {/* delete confirmation message */}
      <MuiPopup
        title={"Permission"}
        // subTitle={"Are you sure want to remove this " + headerLabel + "?"}
        variant="h1"
        openPopup={onDeleteId}
        onClose={onCloseDeletePopup}
        actions={<>
          <Button
            variant="contained"
            size='small'
            children="Yes"
            onClick={handleOnDelete}
          />
          <Button
            size='small'
            children="No"
            onClick={onCloseDeletePopup}
          />
        </>}
      >
        <Typography variant="h6" >
          {"Are you sure want to remove this " + (headerLabel || "item") + "?"}
        </Typography>

        {(getTreeData || treeData) &&
          <MuiRichTreeView
            getTreeData={getTreeData}
            apiKey={treeApiKey}
            items={treeData}
            apiRequest={{ id: onDeleteId }}
            label={treeViewLabel}
          />
        }
      </MuiPopup>
    </MuiPage>
  );
};
